import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { convertShift, formatClockRange, SHIFT_DISPLAY_ZONES, SHIFT_SOURCE_ZONE } from '@nest-recruit/shared';
import { useNow } from '../../../lib/use-now';
import styles from './ShiftPreview.module.css';

/**
 * "6:00 pm–3:00 am BD = 8:00 am–5:00 pm EDT New York" — the conversion helper D14 asks for.
 * Display only: nothing ever calculates with it. Converted for today, because the
 * answer moves with daylight saving and today's is the one a candidate reads.
 */

const MS_PER_HOUR = 60 * 60 * 1000;

interface ShiftPreviewProps {
  readonly start: string | null;
  readonly end: string | null;
  readonly zone: string | null;
}

export function ShiftPreview({ start, end, zone }: ShiftPreviewProps): ReactNode {
  const now = useNow(MS_PER_HOUR);
  if (!start || !end || !zone) {
    return <p className={styles.empty}>Choose the start, the end and a client time zone to see the conversion.</p>;
  }

  const today = bdDate(now);
  const converted = convertShift({ start, end }, SHIFT_SOURCE_ZONE, zone, today);
  const place = SHIFT_DISPLAY_ZONES.find((candidate) => candidate.value === zone)?.label ?? zone;

  return (
    <p className={styles.preview} aria-live="polite">
      <span className={styles.side}>
        {formatClockRange(start, end)} <span className={styles.zone}>BD</span>
      </span>
      <ArrowRight size={16} aria-hidden className={styles.arrow} />
      <span className={styles.side}>
        {formatClockRange(converted.start, converted.end)} <span className={styles.zone}>{converted.zoneAbbreviation}</span> {place}
      </span>
      {converted.startDayShift !== 0 && (
        <span className={styles.note}>
          Starts the {converted.startDayShift === -1 ? 'previous' : 'next'} day in {place}
        </span>
      )}
    </p>
  );
}

/** Today's calendar date in Dhaka, `YYYY-MM-DD`. */
function bdDate(at: number): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: SHIFT_SOURCE_ZONE }).format(at);
}
